
import {
  defineComponent, ref, watch, PropType,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import Dropdown from 'primevue/dropdown';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Button from 'primevue/button';
import Loader from '@/components/layout/Loader.vue';
import { getAvailableCollections, getDefaultIterator } from '@/composables/template-builder/loopCollections';
import type { CollectionGroup, LoopConfiguration } from '@/types/loop-configuration';
import { getCollectionSchema } from '@/composables/template-builder/collectionContext';
import Tooltip from 'primevue/tooltip';

export default defineComponent({
  name: 'LoopConfigurationModal',

  components: {
    Dialog,
    Dropdown,
    InputText,
    InputNumber,
    Button,
    Loader,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    visible: {
      type: Boolean,
      required: true,
    },

    modelValue: {
      type: Object as PropType<LoopConfiguration | null>,
      default: null,
    },

    title: {
      type: String,
      default: '',
    },

    showDeleteButton: {
      type: Boolean,
      default: false,
    },

    contexts: {
      type: Array as PropType<string[]>,
      default: () => ['contact', 'order', 'cart', 'product', 'visitor'],
    },
  },

  emits: {
    'update:visible': (value: boolean) => typeof value === 'boolean',
    save: (config: LoopConfiguration) => config !== null,
    delete: () => true,
  },

  setup(props, { emit }) {
    const { t } = useI18n();

    const localCollection = ref('');
    const localIterator = ref('');
    const localOrderField = ref('');
    const localOrderDirection = ref<'asc' | 'desc'>('asc');
    const localLimit = ref<number>(10);
    const collectionGroups = ref<CollectionGroup[]>([]);
    const originalIterator = ref('');
    const isLoading = ref(false);
    const availableFields = ref<{ label: string; value: string }[]>([]);

    const orderDirections = [
      { label: t('templateBuilder.loop.ascending'), value: 'asc' },
      { label: t('templateBuilder.loop.descending'), value: 'desc' },
    ];

    // Load available fields when collection changes
    const loadAvailableFields = async () => {
      if (!localCollection.value) {
        availableFields.value = [];
        return;
      }

      try {
        const { schema } = await getCollectionSchema(localCollection.value);
        if (schema && schema.properties) {
          availableFields.value = schema.properties.map((prop: any) => ({
            label: prop.label || prop.name,
            value: prop.name,
          }));
        } else {
          availableFields.value = [];
        }
      } catch (error) {
        console.error('Error loading fields:', error);
        availableFields.value = [];
      }
    };

    // Load collections when modal opens
    watch(() => props.visible, async (isVisible) => {
      if (isVisible) {
        isLoading.value = true;
        try {
          // Load available collections
          const collections = await getAvailableCollections(props.contexts);
          collectionGroups.value = collections;

          // Initialize form with modelValue
          if (props.modelValue) {
            localCollection.value = props.modelValue.collection;
            localIterator.value = props.modelValue.iterator;
            originalIterator.value = props.modelValue.iterator;
            localOrderField.value = props.modelValue.order?.field || '';
            localOrderDirection.value = props.modelValue.order?.direction || 'asc';
            localLimit.value = props.modelValue.limit || 10;
          } else {
            localCollection.value = '';
            localIterator.value = '';
            originalIterator.value = '';
            localOrderField.value = '';
            localOrderDirection.value = 'asc';
            localLimit.value = 10;
          }

          // Load fields for the selected collection
          if (localCollection.value) {
            await loadAvailableFields();
          }
        } finally {
          isLoading.value = false;
        }
      }
    });

    // Auto-fill iterator when collection changes
    const onCollectionChange = async () => {
      if (localCollection.value) {
        const selectedCollection = collectionGroups.value
          .flatMap((group) => group.items)
          .find((item) => item.value === localCollection.value);

        if (selectedCollection) {
          localIterator.value = selectedCollection.defaultIterator;
        } else {
          localIterator.value = getDefaultIterator(localCollection.value);
        }

        // Load available fields for the new collection
        await loadAvailableFields();

        // Reset order field if it's not in the new collection's fields
        if (localOrderField.value && availableFields.value.length > 0) {
          const fieldExists = availableFields.value.some((f) => f.value === localOrderField.value);
          if (!fieldExists) {
            localOrderField.value = '';
          }
        }
      }
    };

    const handleSave = async () => {
      if (localCollection.value && localIterator.value) {
        isLoading.value = true;
        try {
          const saveConfig: LoopConfiguration = {
            enabled: true,
            collection: localCollection.value,
            iterator: localIterator.value,
            limit: localLimit.value || 10,
          };

          // Add order if field is selected
          if (localOrderField.value) {
            saveConfig.order = {
              field: localOrderField.value,
              direction: localOrderDirection.value,
            };
          }

          emit('save', saveConfig);

          // Wait a bit to ensure the save event is processed before closing
          await new Promise((resolve) => setTimeout(resolve, 50));

          emit('update:visible', false);
        } finally {
          isLoading.value = false;
        }
      }
    };

    const handleDelete = () => {
      emit('delete');
      emit('update:visible', false);
    };

    const handleCancel = () => {
      emit('update:visible', false);
    };

    const getCollectionLabel = (value: string) => {
      const collection = collectionGroups.value
        .flatMap((group) => group.items)
        .find((item) => item.value === value);
      return collection?.label || value;
    };

    return {
      t,
      localCollection,
      localIterator,
      localOrderField,
      localOrderDirection,
      localLimit,
      collectionGroups,
      availableFields,
      orderDirections,
      isLoading,
      onCollectionChange,
      handleSave,
      handleDelete,
      handleCancel,
      getCollectionLabel,
    };
  },
});
